import { usePagination, UsePaginationProps } from "react-instantsearch"

import ArrowLeftIcon from "../../../../media/icons/arrow-left.svg"
import ArrowRightIcon from "../../../../media/icons/arrow-right.svg"

export default function Pagination(props: UsePaginationProps) {
  const {
    currentRefinement,
    nbPages,
    isFirstPage,
    isLastPage,
    refine,
    createURL,
  } = usePagination(props)

  const previousPageIndex = currentRefinement - 1
  const nextPageIndex = currentRefinement + 1

  if (nbPages <= 1) {
    return null
  }

  const generatePageNumbers = () => {
    const pageNumbers: (number | "ellipsis-start" | "ellipsis-end")[] = []
    const current = currentRefinement
    const total = nbPages

    // Always show first page
    pageNumbers.push(0)

    // Calculate range around current page (2 on each side)
    const rangeStart = Math.max(1, current - 1)
    const rangeEnd = Math.min(total - 2, current + 1)

    // Add ellipsis after first page if needed
    if (rangeStart > 1) {
      pageNumbers.push("ellipsis-start")
    }

    // Add 2 pages around current
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pageNumbers.push(i)
    }

    // Add ellipsis before last page if needed
    if (rangeEnd < total - 2) {
      pageNumbers.push("ellipsis-end")
    }

    // Show last page
    pageNumbers.push(total - 1)

    return pageNumbers
  }

  const pageNumbers = generatePageNumbers()

  return (
    <nav aria-label="pagination" className="mt-24 tablet:mt-36 desktop:mt-48">
      <ul className="flex items-center justify-center gap-x-20">
        <PaginationItem
          className="button button--primary button--round"
          isDisabled={isFirstPage}
          href={createURL(previousPageIndex)}
          onClick={() => refine(previousPageIndex)}
        >
          <ArrowLeftIcon className="size-20" color="white" />
        </PaginationItem>
        {pageNumbers.map(page => {
          if (page === "ellipsis-start" || page === "ellipsis-end") {
            return (
              <li key={page} className="p-6">
                <span className="text-black-70">...</span>
              </li>
            )
          }

          const label = page + 1
          const isCurrentPage = page === currentRefinement

          return (
            <PaginationItem
              key={page}
              aria-label={`Page ${label}`}
              href={createURL(page)}
              onClick={() => refine(page)}
            >
              <span
                className={`${isCurrentPage ? "p-6 font-bold text-black underline underline-offset-[4px]" : "p-6 text-black-70 hover:text-black hover:underline underline-offset-[4px]"}`}
              >
                {label}
              </span>
            </PaginationItem>
          )
        })}
        <PaginationItem
          className="button button--primary button--round"
          isDisabled={isLastPage}
          href={createURL(nextPageIndex)}
          onClick={() => refine(nextPageIndex)}
        >
          <ArrowRightIcon className="size-20" color="white" />
        </PaginationItem>
      </ul>
    </nav>
  )
}

type PaginationItemProps = {
  href: string
  onClick: React.MouseEventHandler
  children: React.ReactNode
  isDisabled?: boolean
  className?: string
}

function PaginationItem({
  href,
  onClick,
  children,
  isDisabled = false,
  className = "",
}: PaginationItemProps) {
  return (
    <li className={isDisabled ? "hidden" : ""}>
      {isDisabled ? (
        <span className={className}>{children}</span>
      ) : (
        <a
          className={className}
          href={href}
          onClick={event => {
            event.preventDefault()
            onClick(event)
          }}
        >
          {children}
        </a>
      )}
    </li>
  )
}
